import { expValEquals } from '@confluence/feature-experiments';

import { useIsSticky } from './useIsSticky.ts';
import { useStickyTableHeaderObserver } from './useStickyTableHeaderObserver.ts';

/**
 * useEffect to handle sticky comment behavior when attached to a sticky table header.
 * As the user scrolls, the IntersectionObserver triggers the callback the moment the table header starts/stops being sticky
 */
export const useShouldCommentSidebarBeSticky = ({
	annotationElement,
	isEditor,
}: {
	annotationElement?: HTMLElement | null;
	isEditor: boolean;
}): {
	isSticky: boolean;
} => {
	const classToCheck = '.pm-table-container';

	let elementToObserve: HTMLElement | null | undefined = undefined;

	// elements to observe, these are different than elements that determine if the table is sticky
	if (isEditor) {
		if (expValEquals('platform_editor_table_sticky_header_improvements', 'cohort', 'test_with_overflow')) {
			elementToObserve = annotationElement
				?.closest(classToCheck)
				?.querySelector('.pm-table-wrapper > table > tbody > tr');
		} else {
			elementToObserve = annotationElement
				?.closest(classToCheck)
				?.parentElement?.querySelector('.pm-table-sticky-sentinel-top');
		}
	} else {
		elementToObserve = annotationElement?.closest(classToCheck);
	}

	const [isSticky, setIsSticky] = useIsSticky({ isEditor, elementToObserve });

	useStickyTableHeaderObserver({
		setIsSticky,
		isEditor,
		elementToObserve,
	});

	// Check if annotationElement is a child of tr (th element) and th's immediate parent is tbody
	const thElement = annotationElement?.closest('th');
	if (thElement) {
		const trElement = thElement.parentElement;
		const tbodyElement = trElement?.parentElement;

		// If tr is the first child of tbody, return false
		if (tbodyElement?.firstElementChild !== trElement) {
			return { isSticky: false };
		}
	}

	// if annotation element is inside a nested table, then return false
	if (annotationElement?.closest('.pm-table-nested-table-with-controls')) {
		return { isSticky: false };
	}

	// if annotation element is not inside a table header, then return false
	if (!annotationElement?.closest('th')) {
		return { isSticky: false };
	}

	// Remove debug console logs
	return { isSticky };
};
